import React, { useState } from 'react';
import { captureException } from '@sentry/nextjs';
import styled, { css, useTheme } from 'styled-components';
import {
  ButtonStyles,
  ProductCardButton,
  ProductCardSection,
  useSnackbar,
} from '@sparknz/spark-pattern-library';

import { useProductDetails } from 'journey/productDetails/provider';
import { PlanOfferDetail } from 'journey/productDetails/typings';
import {
  getPlanCardCtaLabel,
  isPlanSelected,
} from 'journey/productDetails/utils/plan.utils';
import { PlanCard } from 'shared/components';
import { GENERIC_ERROR_MESSAGE } from 'shared/constants/messages.constants';

const ProductCardBottomSection = styled(ProductCardSection)`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: flex-start;
`;

const StyledProductCardButton = styled(ProductCardButton)<{
  isSelected: boolean;
}>`
  ${({ isSelected }) =>
    isSelected &&
    css`
      ${ButtonStyles.PrimaryButtonActiveStyle}
    `}
`;

interface ProductPlanCardProps {
  offer: PlanOfferDetail;
  selectedPlanId: string;
  isAuthenticatedSparkUser: boolean;
  isEligibilityCheckRequired: boolean;
  isCheckInProgress: boolean;
  handlePlanSelect(): Promise<void>;
  handleInfoIconClick(contentKey: string): void;
}

const ProductPlanCard = ({
  offer,
  selectedPlanId,
  isAuthenticatedSparkUser,
  isEligibilityCheckRequired,
  isCheckInProgress,
  handlePlanSelect,
  handleInfoIconClick,
}: ProductPlanCardProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { palette } = useTheme();
  const { showSnackbar } = useSnackbar();

  const {
    ifpEligibility: { existingOfferId, isChangePlan },
  } = useProductDetails();

  const isSelected = isPlanSelected({
    offerId: offer.id,
    planId: selectedPlanId,
    isChangePlan,
    existingOfferId,
  });

  const isDisabled = isSelected || (isCheckInProgress && !isLoading);

  const label = getPlanCardCtaLabel({
    isChangePlan,
    isSelected,
    palette,
    isEligibilityCheckRequired,
    isAuthenticatedSparkUser,
  });

  const handleClick = async () => {
    try {
      setIsLoading(true);

      await handlePlanSelect();
    } catch (error) {
      captureException(error);

      showSnackbar({ variant: 'error', text: GENERIC_ERROR_MESSAGE });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PlanCard
      {...offer}
      keepMarketingTabHeight={false}
      onInfoIconClick={handleInfoIconClick}
      PlanCardAction={
        <ProductCardBottomSection>
          <StyledProductCardButton
            variant="primary"
            isLoading={isLoading}
            disabled={isDisabled}
            onClick={handleClick}
            isSelected={isSelected}
          >
            {label}
          </StyledProductCardButton>
        </ProductCardBottomSection>
      }
    />
  );
};

export default ProductPlanCard;
